/** @jsxImportSource @emotion/react */
import { FC, useCallback } from 'react';
import { FormikErrors, useFormik } from 'formik';
import { TextFields, Widgets } from '@zip/business-components';
import { useMerchantApplicationContext } from 'contexts/MerchantApplication.context';
import {
  PatchType,
  StepComponentProps,
  type SignUpInfoProps,
} from 'types/interfaces/sign-up/MerchantApplication.types';
import { isFieldError, MappedError } from 'types/common/ApiHelpers';
import { SignUpStepKey } from '../signUpConfig';
import React from 'react';
import { Validators } from 'utils';
import * as formStyles from './SignUpForm.styles';
import { FormHelperText, Link } from '@mui/material';

const signUpInfoInitialValues: SignUpInfoProps = {
  firstName: '',
  lastName: '',
  businessEmail: '',
  mobileNumber: '',
  websiteUrl: '',
  referrer: '',
  privacyPolicyAgreement: false,
  privacyPolicyAgreementDate: null,
};

function validateValues(
  values: SignUpInfoProps
): FormikErrors<SignUpInfoProps> {
  const errors: FormikErrors<SignUpInfoProps> = {};

  if (!values.firstName?.trim()) {
    errors.firstName = 'Please enter first name';
  }

  if (!values.lastName?.trim()) {
    errors.lastName = 'Please enter last name';
  }

  const businessEmail = values.businessEmail?.trim();
  if (!businessEmail) {
    errors.businessEmail = 'Please enter business email address';
  } else if (!Validators.validateEmail(businessEmail)) {
    errors.businessEmail = 'Please enter valid business email address';
  }

  const mobileNumber = values.mobileNumber?.trim();
  if (!mobileNumber) {
    errors.mobileNumber = 'Please enter mobile number';
  } else if (mobileNumber === '0400000000') {
    errors.mobileNumber = 'Please enter a valid mobile number';
  } else if (!Validators.validateMobile(mobileNumber)) {
    errors.mobileNumber = 'Mobile number must start with "04" and be 10 digits';
  }

  const websiteUrl = values.websiteUrl?.trim();
  if (!websiteUrl) {
    errors.websiteUrl = 'Please enter website URL';
  } else if (!Validators.validateUrlWithOptionalProtocol(websiteUrl)) {
    errors.websiteUrl = 'Please enter a valid website URL';
  }

  if (!values.privacyPolicyAgreement) {
    errors.privacyPolicyAgreement = 'You must accept the Privacy Policy';
  }

  return errors;
}

export const SignUpInfoStepModule: FC<StepComponentProps> = ({
  provideSubmit,
  onError,
}) => {
  const { setStepData, getStepData } = useMerchantApplicationContext();
  const [submitAttempted, setSubmitAttempted] = React.useState(false);
  const signUpInfo = getStepData(SignUpStepKey.SignUpInfo);
  const initialValues = React.useMemo(
    () => ({
      ...signUpInfoInitialValues,
      ...(signUpInfo ?? {}),
    }),
    [signUpInfo]
  );

  const formik = useFormik({
    initialValues,
    validate: validateValues,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,

    onSubmit: async (values) => {
      setStepData(SignUpStepKey.SignUpInfo, values, PatchType.SignUpInfo, true);
    },
  });

  React.useEffect(() => {
    if (submitAttempted) {
      formik.validateForm();
    }

    setStepData(SignUpStepKey.SignUpInfo, formik.values, PatchType.SignUpInfo);
  }, [formik.values, submitAttempted, setStepData]);

  const handleError = useCallback(
    (error: MappedError) => {
      if (isFieldError(error)) {
        Object.entries(error.fieldErrors).forEach(([field, message]) => {
          formik.setFieldError(field, message);
          formik.setFieldTouched(field, true, false);
        });
        return;
      }
      onError?.(error);
    },
    [formik, onError]
  );

  React.useEffect(() => {
    provideSubmit?.(async () => {
      setSubmitAttempted(true);

      const errors = await formik.validateForm();

      if (Object.keys(errors).length > 0) {
        formik.setTouched(
          Object.keys(formik.values).reduce(
            (acc, key) => ({ ...acc, [key]: true }),
            {}
          ),
          false
        );
        return false;
      }

      await formik.submitForm();
      return true;
    }, handleError);
  }, [provideSubmit, formik, handleError]);

  return (
    <form>
      <TextFields.Outlined
        type="text"
        name="firstName"
        label="First name"
        css={formStyles.textfield}
        className="fs-mask"
        fullWidth
        value={formik.values.firstName}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.firstName && Boolean(formik.errors.firstName)}
        helperText={Validators.renderFieldError(
          formik.touched.firstName,
          formik.errors.firstName,
          submitAttempted
        )}
      />

      <TextFields.Outlined
        type="text"
        name="lastName"
        label="Last name"
        css={formStyles.textfield}
        className="fs-mask"
        fullWidth
        value={formik.values.lastName}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.lastName && Boolean(formik.errors.lastName)}
        helperText={Validators.renderFieldError(
          formik.touched.lastName,
          formik.errors.lastName,
          submitAttempted
        )}
      />

      <TextFields.Outlined
        type="text"
        name="businessEmail"
        label="Business email address"
        css={formStyles.textfield}
        className="fs-mask"
        fullWidth
        value={formik.values.businessEmail}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={
          formik.touched.businessEmail && Boolean(formik.errors.businessEmail)
        }
        helperText={Validators.renderFieldError(
          formik.touched.businessEmail,
          formik.errors.businessEmail,
          submitAttempted
        )}
      />

      <TextFields.Outlined
        type="text"
        name="mobileNumber"
        label="Mobile number"
        css={formStyles.textfield}
        className="fs-mask"
        fullWidth
        value={formik.values.mobileNumber}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={
          formik.touched.mobileNumber && Boolean(formik.errors.mobileNumber)
        }
        helperText={Validators.renderFieldError(
          formik.touched.mobileNumber,
          formik.errors.mobileNumber,
          submitAttempted
        )}
      />

      <TextFields.Outlined
        type="text"
        name="websiteUrl"
        label="Website URL"
        css={formStyles.textfield}
        className="fs-mask"
        fullWidth
        value={formik.values.websiteUrl}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.websiteUrl && Boolean(formik.errors.websiteUrl)}
        helperText={Validators.renderFieldError(
          formik.touched.websiteUrl,
          formik.errors.websiteUrl,
          submitAttempted
        )}
      />

      <Widgets.Checkbox
        css={formStyles.privacyPolicyCheckbox}
        checked={formik.values.privacyPolicyAgreement}
        onChange={(e, checked) => {
          formik.setFieldValue('privacyPolicyAgreement', checked);

          formik.setFieldValue(
            'privacyPolicyAgreementDate',
            checked
              ? (formik.values.privacyPolicyAgreementDate ??
                  new Date().toISOString())
              : null
          );
        }}
        onBlur={formik.handleBlur}
        label={
          <p>
            I confirm that I have read and agree to{' '}
            <Link
              href="https://zip.co/au/page/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Zip's Privacy Policy.
            </Link>
          </p>
        }
      />
      {formik.touched.privacyPolicyAgreement &&
        formik.errors.privacyPolicyAgreement && (
          <FormHelperText css={formStyles.helperText}>
            {Validators.renderFieldError(
              formik.touched.privacyPolicyAgreement,
              formik.errors.privacyPolicyAgreement,
              submitAttempted
            )}
          </FormHelperText>
        )}
    </form>
  );
};

export default SignUpInfoStepModule;
